import { useState } from "react";
import { Link } from "react-router-dom";
import { EmptyState, ProgressBar } from "@/components/new-look/blocks";
import { useTeamRollup } from "@/hooks/queries/use-team-rollup";
import { useTeams } from "@/hooks/queries/use-teams";
import { usePageTitle } from "@/hooks/use-page-title";
import { getInitials } from "@/lib/get-initials";
import type { RollupMachine, TeamRollupMember } from "@/lib/api/team-rollup";
import { cn } from "@/lib/utils";

/**
 * The new look's Team board (docs/new-look/PLAN.md, step 6.2): today's team
 * rollup as one column per person, with their machines, what they're working
 * on and how far along they are.
 */

const MACHINE_STATE: Record<RollupMachine["readiness"], { label: string; dot: string }> = {
  ready: { label: "Ready", dot: "bg-green-500" },
  "runtime-not-found": { label: "No AI tool found", dot: "bg-amber-500" },
  offline: { label: "Offline", dot: "bg-muted-foreground/50" },
};

/** What a machine shows: Working while it runs a job, else its readiness. */
export function machineState(m: RollupMachine): { label: string; dot: string } {
  if (m.busy && m.readiness !== "offline") {
    return { label: "Working", dot: "animate-pulse bg-blue-500" };
  }
  return MACHINE_STATE[m.readiness];
}

/** People with something going on first, then by name. */
export function boardOrder(members: readonly TeamRollupMember[]): TeamRollupMember[] {
  const busy = (m: TeamRollupMember) =>
    (m.machines ?? []).some((x) => x.busy) ? 2 : m.activeFeatures.length > 0 ? 1 : 0;
  return [...members].sort((a, b) => busy(b) - busy(a) || a.user.name.localeCompare(b.user.name));
}

function PersonColumn({ member }: { member: TeamRollupMember }) {
  const { user, progress, activeFeatures, activeEpics } = member;
  const machines = member.machines ?? [];
  return (
    <section
      aria-label={user.name}
      className="flex w-[280px] shrink-0 flex-col gap-4 rounded-2xl border border-border bg-background p-4 sm:w-[300px]"
    >
      <header className="flex items-center gap-3">
        {user.avatarUrl ? (
          <img src={user.avatarUrl} alt="" className="h-9 w-9 shrink-0 rounded-full object-cover" />
        ) : (
          <span
            aria-hidden="true"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary"
          >
            {getInitials(user.name)}
          </span>
        )}
        <h2 className="min-w-0 flex-1 truncate text-[15px] font-semibold text-foreground">
          {user.name}
        </h2>
      </header>

      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Machines
        </h3>
        {machines.length === 0 ? (
          <p className="mt-1.5 text-sm text-muted-foreground">None connected</p>
        ) : (
          <ul className="mt-1.5 flex flex-col gap-1.5">
            {machines.map((m) => {
              const state = machineState(m);
              return (
                <li key={m.id} className="flex items-center gap-2 text-sm">
                  <span
                    aria-hidden="true"
                    className={cn("h-2 w-2 shrink-0 rounded-full", state.dot)}
                  />
                  <span className="min-w-0 flex-1 truncate text-foreground">{m.name}</span>
                  <span className="shrink-0 text-[13px] text-muted-foreground">{state.label}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div>
        <div className="flex items-baseline justify-between gap-2 text-sm">
          <span className="text-muted-foreground">
            {progress.totalAssigned > 0
              ? `${String(progress.completed)} of ${String(progress.totalAssigned)} done`
              : "Nothing assigned"}
          </span>
          {progress.blocked > 0 && (
            <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-800 dark:bg-red-500/15 dark:text-red-200">
              {progress.blocked} blocked
            </span>
          )}
        </div>
        {progress.totalAssigned > 0 && (
          <ProgressBar
            value={progress.percentComplete}
            label={`${user.name}'s progress`}
            className="mt-2"
          />
        )}
      </div>

      <div className="min-w-0">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Working on
        </h3>
        {activeFeatures.length === 0 ? (
          <p className="mt-1.5 text-sm text-muted-foreground">Nothing in progress</p>
        ) : (
          <ul className="mt-1.5 flex flex-col">
            {activeFeatures.map((f) => (
              <li key={f.id}>
                <Link
                  to={`/features/${f.id}`}
                  className="flex min-h-11 flex-col justify-center rounded-lg px-2 py-1.5 hover:bg-muted"
                >
                  <span className="flex items-center gap-2">
                    <span className="min-w-0 flex-1 truncate text-sm text-foreground">
                      {f.title}
                    </span>
                    {f.blocked && (
                      <span className="shrink-0 rounded-full bg-red-100 px-1.5 text-[11px] font-semibold text-red-800 dark:bg-red-500/15 dark:text-red-200">
                        Blocked
                      </span>
                    )}
                  </span>
                  <span className="truncate text-xs text-muted-foreground">{f.epicName}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      {activeEpics.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {activeEpics.map((e) => (
            <Link
              key={e.id}
              to={`/blueprints/${e.id}`}
              title={e.name}
              className="max-w-full truncate rounded-full bg-muted px-2.5 py-1 text-xs text-muted-foreground hover:text-foreground"
            >
              {e.name}
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

export function TeamBoardPage() {
  usePageTitle("Team");
  const [teamId, setTeamId] = useState("");
  const { data, isLoading, isError } = useTeamRollup(teamId || undefined);
  const { data: teamsData } = useTeams();
  const teams = teamsData?.data ?? [];
  const members = boardOrder(data?.members ?? []);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">Team</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Who's working on what, and on which machine.
            {data
              ? ` ${String(data.memberCount)} ${data.memberCount === 1 ? "person" : "people"}.`
              : ""}
          </p>
        </div>
        {teams.length > 0 && (
          <select
            value={teamId}
            onChange={(e) => {
              setTeamId(e.target.value);
            }}
            aria-label="Show a team"
            className="min-h-11 rounded-lg border border-border bg-background px-3 text-sm text-foreground"
          >
            <option value="">Everyone</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {isLoading ? (
        <div aria-busy="true" aria-label="Loading the team" className="flex gap-4 overflow-hidden">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-80 w-[280px] shrink-0 animate-pulse rounded-2xl bg-muted" />
          ))}
        </div>
      ) : isError ? (
        <EmptyState
          title="Couldn't load the team"
          body="Check your connection and refresh the page."
        />
      ) : members.length === 0 ? (
        <EmptyState
          title="No one to show"
          body="There's no one in this team yet, or you can't see them."
        />
      ) : (
        <div className="-mx-4 overflow-x-auto px-4 pb-2 sm:mx-0 sm:px-0">
          <div className="flex items-start gap-4">
            {members.map((m) => (
              <PersonColumn key={m.user.id} member={m} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
